import { DatePipe } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { HandleLoadingState } from '../../../../decorators/handle-loading-state.decorator';
import { ClaimRow } from '../../../../models/api/view/claim-view/claim-row';
import { Earning } from '../../../../models/api/view/claim/hr/earnings/earning';
import { EarningColumns } from '../../../../models/api/view/claim/hr/earnings/earning-columns';
import { EarningInput } from '../../../../models/api/view/claim/hr/earnings/earning-input';
import { ExportService } from '../../../../services/export/export.service';
import { ClaimExportMapper } from '../../../../utilities/claim-export-mapper';
import { BaseComponent } from '../../../base/base.component';
import { ButtonComponent } from '../../../core/buttons/button.component';
import { ExportFormat } from '../../../core/buttons/export-button.component';
import { RangeComponent } from '../../../core/controls/range.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { ColumnComponent } from '../../../core/table/column.component';
import { TableComponent } from '../../../core/table/table.component';

@Component({
  selector: 'app-earnings',
  imports: [
    TableComponent,
    ColumnComponent,
    RouterModule,
    PageHeaderComponent,
    RangeComponent,
    ButtonComponent,
  ],
  template: `
    <app-page-header label="Earnings" />
    <div class="tw-flex tw-gap-4 tw-items-center">
      <app-range
        right1
        [control]="fg.controls.beginDate"
        [toControl]="fg.controls.endDate"
        label="Date range"
      />
      <app-button
        label="search"
        type="submit"
        (click)="searchClick()"
        [disabled]="
          (fg.value.beginDate !== null && !fg.value.endDate) ||
          (!fg.value.beginDate && fg.value.endDate !== null)
        "
      />
    </div>
    <app-table
      [dataSource]="earning"
      [pageSize]="10"
      [scrollable]="false"
      dataKey="id"
      recordLabel="Earning"
      [showExport]="true"
      [error]="error"
      (export)="onExport($event)"
    >
      <app-column name="beginDate" label="Begin date" format="d" />
      <app-column name="endDate" label="End date" format="d" />
      <app-column name="amount" label="Amount" format="c" />
      <app-column name="type" label="Type" />
      <app-column name="fmla" label="FMLA" />
      <app-column name="frequency" label="Frequency" />
      <app-column name="value" label="Value" />
      <app-column name="unit" label="Unit" />
      <app-column name="offCycle" label="Off cycle" format="b" />
      <app-column name="payrollDate" label="Payroll date" format="d" />
    </app-table>
  `,
  styles: ``,
})
export class EarningsComponent extends BaseComponent implements OnInit {
  private datePipe = inject(DatePipe);
  exportService = inject(ExportService);

  result: any;
  earningInput: EarningInput;
  claim: ClaimRow;
  earning: Earning[] = [];
  earningColumns: EarningColumns[] = [];
  dateChecked = false;
  error: any = null;

  fg = new FormGroup({
    beginDate: new FormControl<Date>(null),
    endDate: new FormControl<Date>(null),
  });

  @HandleLoadingState({ useViewLoading: true })
  async ngOnInit() {
    await this.searchClick();
  }

  async searchClick() {
    if (this.fg.valid) {
      this.fg.markAllAsTouched();
      this.claim = this.view.currentClaim();
      this.earningInput = {
        beginDate: this.fg.value.beginDate,
        endDate: this.fg.value.endDate,
      };
      this.earning = await this.api.earnings.getEarnings(
        this.claim.recordSource,
        this.claim.recordId,
        this.earningInput,
      );
    }
  }

  async onExport(format: ExportFormat) {
    const rows = this.earning;
    const earningColumns = this.earningColumns;
    const columns = [
      {
        propertyName: 'beginDate',
        headerText: 'Begin date',
        format: 'date',
      },
      {
        propertyName: 'endDate',
        headerText: 'End date',
        format: 'date',
      },
      {
        propertyName: 'amount',
        headerText: 'Amount',
        format: 'currency',
      },
      {
        propertyName: 'type',
        headerText: 'Type',
      },
      {
        propertyName: 'fmla',
        headerText: 'FMLA',
      },
      {
        propertyName: 'frequency',
        headerText: 'Frequency',
      },
      {
        propertyName: 'value',
        headerText: 'Value',
      },
      {
        propertyName: 'unit',
        headerText: 'Unit',
      },
      {
        propertyName: 'offCycle',
        headerText: 'Off cycle',
      },
      {
        propertyName: 'payrollDate',
        headerText: 'Payroll date',
        format: 'date',
      },
      ...earningColumns,
    ];

    const formattedDate = this.datePipe.transform(new Date(), 'yyyyMMddHHmm');
    const filename = `Earnings_${formattedDate}.${format}`;

    if (format === 'csv') {
      const csvData = ClaimExportMapper.toCsvExport(rows, columns, 'Earnings');
      await this.exportService.exportCsv(csvData, filename);
    } else if (format === 'xlsx') {
      const excelData = ClaimExportMapper.toExcelExport(
        rows,
        columns,
        'Earnings',
        'OmniInsights Export',
      );
      await this.exportService.exportExcel(excelData, filename);
    }
  }
}
